// ────────────────────────────────────────────────────────────────────
// MaintenanceBanner — Feb 2026.
// Polls /api/settings/maintenance every 60s and shows a persistent
// dismissible banner at the top of the Dashboard when maintenance mode
// is on. Reads existing looks + client cards continue to work; only
// new generation buttons are gated (by the backend HTTP 503 response).
// ────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;

const _DEFAULT_MSG = (
  "TailorFlow is temporarily paused for maintenance. Your existing looks "
  + "and client cards remain fully accessible — new generations will "
  + "resume shortly. Thank you for your patience."
);

export function useMaintenanceMode() {
  const [state, setState] = useState({ enabled: false, message: '' });
  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/settings/maintenance`);
        if (alive) setState({ enabled: !!data?.enabled, message: data?.message || '' });
      } catch { /* silent — network hiccup shouldn't disable the app */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return state;
}

export default function MaintenanceBanner() {
  const { enabled, message } = useMaintenanceMode();
  if (!enabled) return null;
  const copy = (message && message.trim()) || _DEFAULT_MSG;
  return (
    <div
      className="w-full bg-amber-950/40 border-b border-amber-500/40"
      data-testid="maintenance-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-amber-400 font-semibold mb-0.5">
            Maintenance in progress
          </p>
          <p className="text-xs text-amber-200 leading-relaxed">{copy}</p>
        </div>
      </div>
    </div>
  );
}
